import * as React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// hooks
import { useModalData } from 'hooks/index'
// constants
import { REVIEW_STATUSES } from 'constants/review'
import { Modals, ENTITIES } from 'constants/index'
// graphql
import { FIELD_VALUE_UPSERT_MUTATION } from 'graphql/objects/mutations'
import { REVIEW_CREATE_MUTATION, REVIEW_UPDATE_MUTATION } from 'graphql/reviews/mutations'
// types
import { IReview, IReviewResponse } from 'types/graphql'
// utils
import get from 'lodash/get'
import moment from 'moment'
import { getRefetchQueriesForReviews, mapFormPicklistsToFieldValues } from 'utils/reviews'

type Props = React.PropsWithChildren<unknown>

type ReviewCreateResponse = {
  reviewCreate: IReviewResponse
}

interface IContext {
  createReview: (data?: any) => Promise<IReview>
  reviewStatus: React.MutableRefObject<string>
  updateReview: (data: any) => Promise<void>
}

export const CreateReviewAPIContext = React.createContext<IContext>({} as any)

const CreateReviewAPI: React.FC<Props> = ({ children }) => {
  const [reviewCreateMutation] = useMutation<ReviewCreateResponse>(REVIEW_CREATE_MUTATION)
  const [reviewUpdateMutation] = useMutation(REVIEW_UPDATE_MUTATION)
  const [fieldValueUpsertMutation] = useMutation(FIELD_VALUE_UPSERT_MUTATION)

  const upsertPicklistValues = async (reviewId: string, data, refetchQueries, fieldValueMap = []) => {
    const input = mapFormPicklistsToFieldValues(data, fieldValueMap)

    if (!reviewId || !input.length) {
      return
    }

    await fieldValueUpsertMutation({ variables: { entityId: reviewId, input }, refetchQueries })
  }

  const {
    modalData: { entity, review }
  } = useModalData({ modalType: Modals.REVIEW_CREATE_MODAL })

  const reviewStatus: any = React.useRef(REVIEW_STATUSES.NOT_STARTED)

  const createReview = async (data) => {
    const refetchQueries = getRefetchQueriesForReviews(entity)

    const {
      data: {
        reviewCreate: { data: createdReview }
      }
    } = await reviewCreateMutation({
      variables: {
        input: {
          dueAt: review.dueDate.toISOString(),
          startPeriod: moment(review.startPeriod).toISOString(),
          endPeriodAt: moment(review.endPeriod).toISOString(),
          planId: entity.entityType === ENTITIES.PLAN ? entity.id : null,
          planObjectiveId: entity.entityType === ENTITIES.PLAN_OBJECTIVE ? entity.id : null,
          planActionId: entity.entityType === ENTITIES.PLAN_ACTION ? entity.id : null,
          planKPIId: entity.entityType === ENTITIES.PLAN_KPI ? entity.id : null,
          scheduledReviewId: review.scheduledReview.id,
          status: data ? reviewStatus.current : REVIEW_STATUSES.IN_PROGRESS,
          values: {
            goals: get(data, 'goals'),
            doWell: get(data, 'doWell'),
            doBetter: get(data, 'doBetter')
          }
        }
      },
      refetchQueries
    })

    await upsertPicklistValues(get(createdReview, 'id'), data, refetchQueries)

    return createdReview
  }

  const updateReview = async (data) => {
    const refetchQueries = getRefetchQueriesForReviews(entity)

    await reviewUpdateMutation({
      variables: {
        id: review.id,
        input: {
          dueAt: review.dueDate.toISOString(),
          startPeriod: moment(review.startPeriod).toISOString(),
          endPeriodAt: moment(review.endPeriod).toISOString(),
          planId: entity.entityType === ENTITIES.PLAN ? entity.id : null,
          planObjectiveId: entity.entityType === ENTITIES.PLAN_OBJECTIVE ? entity.id : null,
          planActionId: entity.entityType === ENTITIES.PLAN_ACTION ? entity.id : null,
          planKPIId: entity.entityType === ENTITIES.PLAN_KPI ? entity.id : null,
          status: reviewStatus.current,
          values: {
            goals: get(data, 'goals'),
            doWell: get(data, 'doWell'),
            doBetter: get(data, 'doBetter')
          }
        }
      },
      refetchQueries
    })

    await upsertPicklistValues(review.id, data, refetchQueries, review.fieldValueMap)
  }

  const value = { reviewStatus, createReview, updateReview }

  return <CreateReviewAPIContext.Provider value={value}>{children}</CreateReviewAPIContext.Provider>
}

export default CreateReviewAPI
